import { useRef, useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Download, Search, Loader2 } from 'lucide-react'
import { getAllBalances, exportAllBalances, type LeaveException } from '@/api/leaves'
import { listDepartments, type Department } from '@/api/orgstructure'

const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1))

/** Fiscal-year keys to offer: the current one (from the server) and the two before it. */
function yearOptions(current?: number): number[] {
  return current ? [current, current - 1, current - 2] : []
}

/**
 * Every employee's leave quota, used, pending and remaining days per leave type —
 * for admins and anyone whose role has Manage Leave. Mounted as a tab on the
 * Manage Leaves page.
 */
export default function LeaveBalancesReport() {
  const [search, setSearch] = useState('')
  const [departmentId, setDepartmentId] = useState('')
  const [year, setYear] = useState<number | undefined>(undefined)
  const [month, setMonth] = useState('') // YYYY-MM for monthly-quota types; '' = this month
  const [showExport, setShowExport] = useState(false)

  const filters = {
    year, search: search.trim() || undefined, department_id: departmentId || undefined, month: month || undefined,
  }
  const { data: report, isLoading, isError } = useQuery({
    queryKey: ['leaves', 'balances', filters],
    queryFn: () => getAllBalances(filters),
    placeholderData: (prev) => prev,
  })
  const { data: departments = [] } = useQuery({ queryKey: ['departments'], queryFn: () => listDepartments() })
  const exportMut = useMutation({
    mutationFn: (format: 'csv' | 'xlsx') => exportAllBalances(filters, format),
    onSuccess: () => setShowExport(false),
  })

  // The first load (no year chosen) returns the current fiscal year; remember it
  // so the dropdown keeps offering current + previous years after a change.
  const currentFy = useRef<number | undefined>(undefined)
  if (report && year === undefined && currentFy.current === undefined) currentFy.current = report.fiscal_year.key
  const fyChoices = yearOptions(currentFy.current)

  const types = report?.leave_types ?? []
  const hasMonthly = types.some((t) => t.quota_period === 'month')

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email or employee ID…"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#EC5B2C]/30 focus:border-[#EC5B2C]"
          />
        </div>
        <select
          value={departmentId}
          onChange={(e) => setDepartmentId(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value="">All departments</option>
          {departments.map((d: Department) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select
          value={year ?? report?.fiscal_year.key ?? ''}
          onChange={(e) => setYear(Number(e.target.value))}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          {fyChoices.map((y) => (
            <option key={y} value={y}>FY {y}–{String(y + 1).slice(2)}</option>
          ))}
        </select>
        {hasMonthly && (
          <label className="flex items-center gap-2 text-sm text-gray-600" title="Month used for monthly allowances (e.g. WFH)">
            Month
            <input
              type="month"
              value={month || report?.month || ''}
              onChange={(e) => setMonth(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </label>
        )}
        <div className="relative">
          <button
            onClick={() => setShowExport((s) => !s)}
            className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            {exportMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export
          </button>
          {showExport && (
            <div className="absolute right-0 mt-1 w-32 rounded-lg border border-gray-200 bg-white shadow-lg z-10 text-sm">
              <button onClick={() => exportMut.mutate('xlsx')} className="block w-full text-left px-3 py-2 hover:bg-gray-50">Excel (.xlsx)</button>
              <button onClick={() => exportMut.mutate('csv')} className="block w-full text-left px-3 py-2 hover:bg-gray-50">CSV (.csv)</button>
            </div>
          )}
        </div>
      </div>

      {report && (
        <p className="text-xs text-gray-500">
          Fiscal year {report.fiscal_year.start} → {report.fiscal_year.end} · {report.rows.length} employee{report.rows.length === 1 ? '' : 's'} ·
          {' '}<span className="font-medium text-gray-700">remaining</span> = quota − used (approved); pending requests are shown separately.
        </p>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-3 sticky left-0 bg-gray-50 z-10">Employee</th>
              {types.map((t) => (
                <th key={t.key} className="px-4 py-3 whitespace-nowrap">
                  <span className="inline-block h-2 w-2 rounded-full mr-1.5" style={{ background: t.color || '#9ca3af' }} />
                  {t.label}
                  {t.quota_period === 'month' && <span className="ml-1 normal-case font-normal text-gray-400">({report?.month})</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr><td colSpan={types.length + 1} className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>
            )}
            {isError && (
              <tr><td colSpan={types.length + 1} className="px-4 py-10 text-center text-red-500">Couldn't load leave balances.</td></tr>
            )}
            {report && report.rows.length === 0 && (
              <tr><td colSpan={types.length + 1} className="px-4 py-10 text-center text-gray-400">No employees match.</td></tr>
            )}
            {report?.rows.map((r) => (
              <tr key={r.user_id} className="hover:bg-gray-50 align-top">
                <td className="px-4 py-3 sticky left-0 bg-white z-10 min-w-[200px]">
                  <div className="font-medium text-gray-900">{r.name}</div>
                  <div className="text-xs text-gray-500">
                    {[r.employee_id, r.department, r.designation].filter(Boolean).join(' · ') || r.email}
                  </div>
                </td>
                {types.map((t) => {
                  const b = r.balances[t.key]
                  if (!b) {
                    return <td key={t.key} className="px-4 py-3 text-xs text-gray-300" title="Not eligible for this leave type">—</td>
                  }
                  const pct = b.total > 0 ? Math.min(100, (b.used / b.total) * 100) : 0
                  const low = b.remaining <= 0
                  return (
                    <td key={t.key} className="px-4 py-3 min-w-[130px]">
                      <div className={`font-semibold ${low ? 'text-red-600' : 'text-gray-900'}`}>
                        {fmt(b.remaining)} <span className="font-normal text-gray-400">/ {fmt(b.total)} left</span>
                      </div>
                      <div className="mt-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                        <div className="h-full" style={{ width: `${pct}%`, background: t.color || '#EC5B2C' }} />
                      </div>
                      <div className="mt-1 text-[11px] text-gray-500">
                        Used {fmt(b.used)}
                        {b.pending > 0 && <span className="ml-1.5 text-amber-600">· {fmt(b.pending)} pending</span>}
                      </div>
                      {b.period === 'month' && (b.exceptions?.length ?? 0) > 0 && (
                        <OverLimitBadge over={b.over ?? 0} exceptions={b.exceptions!} />
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}


/** Red badge for a monthly allowance that was exceeded: how many days over, and who
 *  approved the exceptions (full list with dates and statuses on hover). */
function OverLimitBadge({ over, exceptions }: { over: number; exceptions: LeaveException[] }) {
  const approvedBy = [...new Set(
    exceptions.filter((e) => e.status === 'approved' && e.approver).map((e) => e.approver as string),
  )]
  const waiting = exceptions.filter((e) => e.status === 'pending').length
  const detail = exceptions
    .map((e) => `${e.date}: ${e.status}${e.approver ? ` (${e.status === 'pending' ? 'waiting for' : 'by'} ${e.approver})` : ''}`)
    .join('\n')
  const parts = [
    over > 0 ? `${fmt(over)} over limit` : null,
    approvedBy.length ? `approved by ${approvedBy.join(', ')}` : null,
    waiting ? `${waiting} awaiting approval` : null,
  ].filter(Boolean)
  return (
    <div className="mt-1 inline-block rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-600 cursor-help" title={detail}>
      ⚠ {parts.join(' · ') || 'Over-limit request'}
    </div>
  )
}
